import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Play, ShieldAlert, Loader2, AlertTriangle, CheckCircle2, Ban } from 'lucide-react'
import { recoverRequestsApi, type RunbookTaskExecuteResult } from '../api'
import type { RecoveryRunbookTask } from '@/types/entities'
import { useToast } from '@/hooks/use-toast'

interface AutomateTaskDialogProps {
    task: RecoveryRunbookTask | null
    requestID: number | string
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Called after a successful run so the parent can refetch the runbook. */
    onExecuted?: () => void
}

// Statuses that mean "cannot run right now" — the engine declined, arm-not-fire.
const BLOCKED_STATUSES = new Set<RunbookTaskExecuteResult['status']>([
    'not_automatable', 'unknown_method', 'blocked', 'missing_inputs', 'error',
])

/**
 * Arm-not-fire dialog for an assisted/automated runbook task. Opening it fetches a
 * PREVIEW from the engine (approve:false) — what would run and whether it can — and
 * nothing executes until the engineer presses Run (approve:true). High blast-radius
 * methods require an explicit acknowledgement first.
 */
export function AutomateTaskDialog({ task, requestID, open, onOpenChange, onExecuted }: AutomateTaskDialogProps) {
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const [ack, setAck] = useState(false)
    const [runResult, setRunResult] = useState<RunbookTaskExecuteResult | null>(null)

    const taskId = task?.id ?? null

    // Fresh state each time the dialog opens (or reopens for a different task).
    useEffect(() => {
        if (open) { setAck(false); setRunResult(null) }
    }, [open, taskId])

    const { data: preview, isLoading: previewLoading, isError: previewError } = useQuery({
        queryKey: ['runbook-task-preview', requestID, taskId],
        queryFn: async () => {
            const res = await recoverRequestsApi.executeRecoveryRunbookTask(requestID, taskId as number, { approve: false })
            return res.data?.data ?? null
        },
        enabled: open && taskId != null,
        staleTime: 0,
        refetchOnWindowFocus: false,
    })

    const runMutation = useMutation({
        mutationFn: async () => {
            const res = await recoverRequestsApi.executeRecoveryRunbookTask(requestID, taskId as number, { approve: true })
            return res.data?.data as RunbookTaskExecuteResult
        },
        onSuccess: (result) => {
            setRunResult(result)
            if (result.status === 'ok') {
                toast({ title: 'Task run', description: `${result.method ?? 'Task'} completed (simulated).` })
                queryClient.invalidateQueries({ queryKey: ['recovery-runbook', requestID] })
                onExecuted?.()
            } else {
                toast({ title: 'Not run', description: result.reason || result.status, variant: 'destructive' })
            }
        },
        onError: (err: any) => {
            toast({ title: 'Execution failed', description: err?.response?.data?.message || err.message, variant: 'destructive' })
        },
    })

    const isHighBlast = preview?.blastRadius === 'high'
    const blocked = !!preview && BLOCKED_STATUSES.has(preview.status)
    const canRun = preview?.status === 'needs_approval' && (!isHighBlast || ack) && !runMutation.isPending && !runResult

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Play className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        Run task {task?.taskNo ?? ''} — automation
                    </DialogTitle>
                    <DialogDescription>
                        Review exactly what will run, then confirm. Nothing runs until you press Run.
                    </DialogDescription>
                </DialogHeader>

                {previewLoading && (
                    <div className="py-8 flex items-center justify-center text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Preparing…
                    </div>
                )}
                {previewError && (
                    <div className="py-6 text-sm text-destructive">Could not prepare this task.</div>
                )}

                {preview && (
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center gap-2">
                            {preview.mode && <Badge variant="secondary">{preview.mode}</Badge>}
                            {preview.method && <Badge variant="outline" className="font-mono">{preview.method}</Badge>}
                            {isHighBlast && (
                                <Badge variant="destructive">
                                    <ShieldAlert className="h-3 w-3 mr-1" /> high impact
                                </Badge>
                            )}
                        </div>

                        {preview.summary && <p className="text-sm text-muted-foreground">{preview.summary}</p>}

                        <div>
                            <div className="text-xs font-medium text-muted-foreground mb-1">What it will run</div>
                            <pre className="text-xs bg-muted rounded-md p-3 max-h-48 overflow-auto whitespace-pre-wrap">
                                {task?.commands?.trim() || '(no command text on this task)'}
                            </pre>
                        </div>

                        {(preview.requires?.length || preview.produces?.length) ? (
                            <div className="grid grid-cols-2 gap-4 text-xs">
                                <div>
                                    <div className="font-medium text-muted-foreground mb-1">Needs inputs</div>
                                    {preview.requires?.length
                                        ? <ul className="space-y-0.5">{preview.requires.map(r => <li key={r} className="font-mono">{r}</li>)}</ul>
                                        : <span className="text-muted-foreground">none</span>}
                                </div>
                                <div>
                                    <div className="font-medium text-muted-foreground mb-1">Produces</div>
                                    {preview.produces?.length
                                        ? <ul className="space-y-0.5">{preview.produces.map(p => <li key={p} className="font-mono">{p}</li>)}</ul>
                                        : <span className="text-muted-foreground">none</span>}
                                </div>
                            </div>
                        ) : null}

                        {blocked && (
                            <div className="flex items-start gap-2 rounded-lg border bg-amber-50 dark:bg-amber-950/20 p-3 text-sm text-amber-700 dark:text-amber-400">
                                <Ban className="h-4 w-4 mt-0.5 shrink-0" />
                                <span>
                                    {preview.reason || `This task can’t run yet (${preview.status}).`}
                                    {preview.missing?.length ? ` Missing: ${preview.missing.join(', ')}.` : ''}
                                </span>
                            </div>
                        )}

                        {preview.status === 'needs_approval' && isHighBlast && !runResult && (
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <Checkbox checked={ack} onCheckedChange={(c) => setAck(c === true)} />
                                I understand this is a high-impact action.
                            </label>
                        )}

                        {runResult && (
                            <div className="rounded-md border p-3 space-y-2">
                                <div className="flex items-center gap-2 text-sm font-medium">
                                    {runResult.status === 'ok'
                                        ? <CheckCircle2 className="h-4 w-4 text-emerald-700 dark:text-emerald-300" />
                                        : <AlertTriangle className="h-4 w-4 text-amber-700 dark:text-amber-400" />}
                                    {runResult.status === 'ok' ? 'Simulated run complete' : `Not run (${runResult.status})`}
                                </div>
                                {runResult.mocked && (
                                    <div className="text-xs text-muted-foreground">Mocked — no real action was performed.</div>
                                )}
                                {runResult.transcript && (
                                    <pre className="text-xs bg-muted rounded p-2 whitespace-pre-wrap">{runResult.transcript}</pre>
                                )}
                                {runResult.factsWritten?.length ? (
                                    <div className="text-xs">Facts recorded: <span className="font-mono">{runResult.factsWritten.join(', ')}</span></div>
                                ) : null}
                                {runResult.warnings?.length ? (
                                    <div className="text-xs text-amber-700 dark:text-amber-400">{runResult.warnings.join(' ')}</div>
                                ) : null}
                                {runResult.reason && runResult.status !== 'ok' && (
                                    <div className="text-xs text-muted-foreground">{runResult.reason}</div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    {!runResult ? (
                        <>
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                            <Button onClick={() => runMutation.mutate()} disabled={!canRun}>
                                {runMutation.isPending
                                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>
                                    : <><Play className="h-4 w-4 mr-2" /> Run</>}
                            </Button>
                        </>
                    ) : (
                        <Button onClick={() => onOpenChange(false)}>Done</Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
